import React, { useEffect, useState } from "react";
import { Plus, Loader2, Plane, Check, X, Ban, Users, Pencil, Minus, Trash2, CalendarClock } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card } from "../components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";

const TYPES = ["Vacaciones", "Permiso personal", "Cita médica", "Ausencia justificada", "Reposición", "Otro"];
const STATUS_BADGE = {
  pending: ["Pendiente", "bg-amber-500/15 text-amber-600"],
  approved: ["Aprobada", "bg-primary/15 text-primary"],
  rejected: ["Rechazada", "bg-destructive/15 text-destructive"],
  cancelled: ["Cancelada", "bg-muted text-muted-foreground"],
};
const empty = { type: "Vacaciones", day_type: "full", date_from: "", date_to: "", reason: "", notes: "", cover_person: "", cover_phone: "" };

function Badge({ status }) {
  const [label, cls] = STATUS_BADGE[status] || ["—", "bg-muted"];
  return <span className={`inline-block px-2 py-0.5 rounded-sm text-[11px] font-bold uppercase ${cls}`}>{label}</span>;
}

export default function Vacations() {
  const { can } = useAuth();
  const [data, setData] = useState({ items: [], balance: 0, assigned: 0, used: 0 });
  const [requests, setRequests] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [assignTarget, setAssignTarget] = useState(null);
  const [assignDays, setAssignDays] = useState("");
  const [adjustTarget, setAdjustTarget] = useState(null);
  const [adjustForm, setAdjustForm] = useState({ delta: "", reason: "" });
  const [periodTarget, setPeriodTarget] = useState(null);
  const [periodForm, setPeriodForm] = useState({ period_start: "", annual_days: "" });
  const [historyTarget, setHistoryTarget] = useState(null);
  const isApprover = can("vacations:approve");

  const load = () => {
    setLoading(true);
    const calls = [api.get("/vacations")];
    if (isApprover) { calls.push(api.get("/vacations/requests")); calls.push(api.get("/vacations/team")); }
    Promise.all(calls).then(([mine, reqs, tm]) => { setData(mine.data); if (reqs) setRequests(reqs.data); if (tm) setTeam(tm.data); }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const save = async () => {
    try { await api.post("/vacations", form); toast.success("Solicitud enviada"); setOpen(false); setForm(empty); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const decide = async (v, decision) => { try { await api.post(`/vacations/${v.id}/decision`, { decision }); toast.success("Actualizado"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const cancel = async (v) => { await api.post(`/vacations/${v.id}/cancel`); load(); };
  const deleteReq = async (v) => { try { await api.delete(`/vacations/${v.id}`); toast.success("Solicitud eliminada"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const clearInactive = async () => { try { const r = await api.post("/vacations/clear-inactive"); toast.success(`${r.data.deleted} solicitud(es) eliminada(s)`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const doAssign = async () => {
    try { await api.put(`/vacations/team/${assignTarget.user_id}/assign`, { days: Number(assignDays) }); toast.success(`Días asignados a ${assignTarget.name}`); setAssignTarget(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doAdjust = async () => {
    try { await api.post(`/vacations/team/${adjustTarget.user_id}/adjust`, { delta: Number(adjustForm.delta), reason: adjustForm.reason }); toast.success("Ajuste aplicado"); setAdjustTarget(null); setAdjustForm({ delta: "", reason: "" }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doPeriod = async () => {
    try { await api.put(`/vacations/team/${periodTarget.user_id}/period`, { period_start: periodForm.period_start, annual_days: Number(periodForm.annual_days) }); toast.success(`Período configurado para ${periodTarget.name}`); setPeriodTarget(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delAdjust = async (uid, aid) => {
    try { await api.delete(`/vacations/team/${uid}/adjust/${aid}`); toast.success("Ajuste eliminado"); const r = await api.get("/vacations/team"); setTeam(r.data); setHistoryTarget((h) => h ? r.data.find((x) => x.user_id === h.user_id) : h); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-stretch gap-4">
        <Card className="rounded-sm border shadow-none p-5 flex items-center gap-4 flex-1" data-testid="vacation-balance">
          <div className="h-12 w-12 flex items-center justify-center bg-primary/10 text-primary rounded-sm"><Plane className="h-6 w-6" /></div>
          <div className="flex-1 grid grid-cols-3 gap-2">
            <div><p className="text-2xl font-heading font-black">{data.balance}</p><p className="text-[10px] uppercase tracking-widest text-muted-foreground">Disponibles</p></div>
            <div><p className="text-2xl font-heading font-black">{data.used}</p><p className="text-[10px] uppercase tracking-widest text-muted-foreground">Usados</p></div>
            <div><p className="text-2xl font-heading font-black">{data.assigned}</p><p className="text-[10px] uppercase tracking-widest text-muted-foreground">Asignados</p></div>
          </div>
        </Card>
        {can("vacations:create") && <Button onClick={() => { setForm(empty); setOpen(true); }} className="rounded-sm gap-2 font-bold uppercase tracking-wider text-xs h-auto px-6" data-testid="new-vacation-btn"><Plus className="h-4 w-4" /> Nueva solicitud</Button>}
      </div>

      <div>
        <h3 className="font-heading font-bold text-lg mb-3">Mis solicitudes</h3>
        <Card className="rounded-sm border shadow-none overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="my-vacations">
              <thead><tr className="bg-muted text-left text-xs uppercase tracking-widest text-muted-foreground">
                <th className="px-4 py-3 font-bold">Tipo</th><th className="px-4 py-3 font-bold">Desde</th><th className="px-4 py-3 font-bold">Hasta</th>
                <th className="px-4 py-3 font-bold">Días</th><th className="px-4 py-3 font-bold">Estado</th><th className="px-4 py-3 font-bold text-right">Acción</th></tr></thead>
              <tbody>
                {data.items.map((v) => (
                  <tr key={v.id} className="border-t border-border hover:bg-muted/50">
                    <td className="px-4 py-2.5 font-medium">{v.type}</td>
                    <td className="px-4 py-2.5 font-mono text-xs">{v.date_from}</td>
                    <td className="px-4 py-2.5 font-mono text-xs">{v.date_to}</td>
                    <td className="px-4 py-2.5 font-mono">{v.days}</td>
                    <td className="px-4 py-2.5"><Badge status={v.status} /></td>
                    <td className="px-4 py-2.5 text-right">{v.status === "pending" && <Button variant="ghost" size="sm" className="text-destructive h-7" onClick={() => cancel(v)} data-testid="cancel-vacation"><Ban className="h-3.5 w-3.5 mr-1" />Cancelar</Button>}</td>
                  </tr>
                ))}
                {data.items.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">Sin solicitudes.</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {isApprover && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Users className="h-5 w-5 text-primary" />
            <h3 className="font-heading font-bold text-lg">Administrar días del equipo</h3>
          </div>
          <Card className="rounded-sm border shadow-none overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="team-balances">
                <thead><tr className="bg-muted text-left text-xs uppercase tracking-widest text-muted-foreground">
                  <th className="px-4 py-3 font-bold">Colaborador</th>
                  <th className="px-4 py-3 font-bold text-center">Asignados</th>
                  <th className="px-4 py-3 font-bold text-center">Ajustes</th>
                  <th className="px-4 py-3 font-bold text-center">Usados</th>
                  <th className="px-4 py-3 font-bold text-center">Disponibles</th>
                  <th className="px-4 py-3 font-bold text-center">Renueva</th>
                  <th className="px-4 py-3 font-bold text-right">Acciones</th></tr></thead>
                <tbody>
                  {team.map((u) => (
                    <tr key={u.user_id} className="border-t border-border hover:bg-muted/50" data-testid={`team-row-${u.user_id}`}>
                      <td className="px-4 py-2.5"><p className="font-medium">{u.name}</p><p className="text-[11px] text-muted-foreground">{u.position || u.role_name}</p></td>
                      <td className="px-4 py-2.5 text-center font-mono">{u.assigned}</td>
                      <td className="px-4 py-2.5 text-center font-mono">
                        {u.adjustments_total !== 0
                          ? <button className={`font-bold underline decoration-dotted ${u.adjustments_total > 0 ? "text-primary" : "text-destructive"}`} onClick={() => setHistoryTarget(u)} data-testid={`adj-history-${u.user_id}`}>{u.adjustments_total > 0 ? "+" : ""}{u.adjustments_total}</button>
                          : <span className="text-muted-foreground">0</span>}
                      </td>
                      <td className="px-4 py-2.5 text-center font-mono">{u.used}</td>
                      <td className="px-4 py-2.5 text-center"><span className={`font-heading font-black text-base ${u.balance < 0 ? "text-destructive" : "text-primary"}`}>{u.balance}</span></td>
                      <td className="px-4 py-2.5 text-center text-xs">
                        {u.next_renewal
                          ? <><span className="font-mono">{u.next_renewal}</span><span className="block text-[11px] text-muted-foreground">+{u.annual_days} días</span></>
                          : <span className="text-muted-foreground">Sin período</span>}
                      </td>
                      <td className="px-4 py-2.5 text-right whitespace-nowrap">
                        <Button size="sm" variant="outline" className="h-7 rounded-sm mr-1 text-xs" onClick={() => { setAssignTarget(u); setAssignDays(String(u.assigned)); }} data-testid={`assign-${u.user_id}`}><Pencil className="h-3.5 w-3.5 mr-1" />Asignar</Button>
                        <Button size="sm" variant="outline" className="h-7 rounded-sm mr-1 text-xs" onClick={() => { setAdjustTarget(u); setAdjustForm({ delta: "", reason: "" }); }} data-testid={`adjust-${u.user_id}`}><Plus className="h-3 w-3" /><Minus className="h-3 w-3 mr-1" />Ajustar</Button>
                        <Button size="sm" variant="outline" className="h-7 rounded-sm text-xs" onClick={() => { setPeriodTarget(u); setPeriodForm({ period_start: u.period_start || "", annual_days: String(u.annual_days || u.assigned || 15) }); }} data-testid={`period-${u.user_id}`}><CalendarClock className="h-3.5 w-3.5 mr-1" />Período</Button>
                      </td>
                    </tr>
                  ))}
                  {team.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">Sin colaboradores.</td></tr>}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {isApprover && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-bold text-lg">Solicitudes del equipo</h3>
            {requests.some((v) => v.status === "cancelled" || v.status === "rejected") && (
              <Button variant="outline" size="sm" className="rounded-sm gap-2 text-xs text-destructive border-destructive/30" onClick={clearInactive} data-testid="clear-inactive-vacations"><Trash2 className="h-3.5 w-3.5" /> Limpiar canceladas/rechazadas</Button>
            )}
          </div>
          <Card className="rounded-sm border shadow-none overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="team-vacations">
                <thead><tr className="bg-muted text-left text-xs uppercase tracking-widest text-muted-foreground">
                  <th className="px-4 py-3 font-bold">Colaborador</th><th className="px-4 py-3 font-bold">Tipo</th><th className="px-4 py-3 font-bold">Fechas</th>
                  <th className="px-4 py-3 font-bold">Días</th><th className="px-4 py-3 font-bold">Estado</th><th className="px-4 py-3 font-bold text-right">Aprobación</th></tr></thead>
                <tbody>
                  {requests.map((v) => (
                    <tr key={v.id} className="border-t border-border hover:bg-muted/50" data-testid={`req-${v.id}`}>
                      <td className="px-4 py-2.5 font-medium">{v.user_name}</td>
                      <td className="px-4 py-2.5">{v.type}</td>
                      <td className="px-4 py-2.5 font-mono text-xs">{v.date_from} → {v.date_to}</td>
                      <td className="px-4 py-2.5 font-mono">{v.days}</td>
                      <td className="px-4 py-2.5"><Badge status={v.status} /></td>
                      <td className="px-4 py-2.5 text-right whitespace-nowrap">
                        {v.status === "pending" ? (
                          <>
                            <Button size="sm" className="h-7 rounded-sm mr-1" onClick={() => decide(v, "approved")} data-testid={`approve-${v.id}`}><Check className="h-3.5 w-3.5" /></Button>
                            <Button size="sm" variant="outline" className="h-7 rounded-sm text-destructive" onClick={() => decide(v, "rejected")} data-testid={`reject-${v.id}`}><X className="h-3.5 w-3.5" /></Button>
                          </>
                        ) : (
                          <div className="flex items-center justify-end gap-2">
                            <span className="text-xs text-muted-foreground">{v.approver}</span>
                            {(v.status === "cancelled" || v.status === "rejected") && (
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 rounded-sm text-destructive" title="Eliminar solicitud" onClick={() => deleteReq(v)} data-testid={`delete-req-${v.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                  {requests.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">Sin solicitudes.</td></tr>}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-md max-h-[90vh] overflow-y-auto" data-testid="vacation-dialog">
          <DialogHeader><DialogTitle className="font-heading">Nueva solicitud</DialogTitle><DialogDescription>Tu jefe recibirá la solicitud para aprobación.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-widest">Tipo</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}><SelectTrigger className="rounded-sm" data-testid="vac-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-widest">Jornada</Label>
                <Select value={form.day_type} onValueChange={(v) => setForm({ ...form, day_type: v })}><SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="full">Día completo</SelectItem><SelectItem value="half">Medio día</SelectItem></SelectContent></Select></div>
              <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-widest">Desde</Label>
                <Input type="date" className="rounded-sm" value={form.date_from} onChange={(e) => setForm({ ...form, date_from: e.target.value })} data-testid="vac-from" /></div>
              <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-widest">Hasta</Label>
                <Input type="date" className="rounded-sm" value={form.date_to} onChange={(e) => setForm({ ...form, date_to: e.target.value })} data-testid="vac-to" /></div>
            </div>
            <Textarea placeholder="Motivo" className="rounded-sm" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Quién cubre" className="rounded-sm" value={form.cover_person} onChange={(e) => setForm({ ...form, cover_person: e.target.value })} />
              <Input placeholder="Tel. durante ausencia" className="rounded-sm" value={form.cover_phone} onChange={(e) => setForm({ ...form, cover_phone: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button className="rounded-sm font-bold" onClick={save} disabled={!form.date_from || !form.date_to} data-testid="save-vacation-btn">Enviar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!assignTarget} onOpenChange={(v) => !v && setAssignTarget(null)}>
        <DialogContent className="rounded-sm max-w-sm" data-testid="assign-dialog">
          <DialogHeader><DialogTitle className="font-heading">Asignar días</DialogTitle>
            <DialogDescription>Días de vacaciones acordados para <strong>{assignTarget?.name}</strong> (base anual).</DialogDescription></DialogHeader>
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-widest">Días asignados</Label>
            <Input type="number" min="0" step="0.5" className="rounded-sm" value={assignDays} onChange={(e) => setAssignDays(e.target.value)} data-testid="assign-days-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setAssignTarget(null)}>Cancelar</Button>
            <Button className="rounded-sm font-bold" onClick={doAssign} disabled={assignDays === ""} data-testid="save-assign-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!adjustTarget} onOpenChange={(v) => !v && setAdjustTarget(null)}>
        <DialogContent className="rounded-sm max-w-sm" data-testid="adjust-dialog">
          <DialogHeader><DialogTitle className="font-heading">Poner o quitar días</DialogTitle>
            <DialogDescription>Suma (ej. 3) o resta (ej. -2) días a <strong>{adjustTarget?.name}</strong>. Queda registrado con motivo.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-widest">Días (+ suma / − resta)</Label>
              <Input type="number" step="0.5" placeholder="Ej. 3 o -2" className="rounded-sm" value={adjustForm.delta} onChange={(e) => setAdjustForm({ ...adjustForm, delta: e.target.value })} data-testid="adjust-delta-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-widest">Motivo</Label>
              <Textarea placeholder="Ej. Bono por antigüedad, adelanto, corrección…" className="rounded-sm" value={adjustForm.reason} onChange={(e) => setAdjustForm({ ...adjustForm, reason: e.target.value })} data-testid="adjust-reason-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setAdjustTarget(null)}>Cancelar</Button>
            <Button className="rounded-sm font-bold" onClick={doAdjust} disabled={!adjustForm.delta || Number(adjustForm.delta) === 0} data-testid="save-adjust-btn">Aplicar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!periodTarget} onOpenChange={(v) => !v && setPeriodTarget(null)}>
        <DialogContent className="rounded-sm max-w-sm" data-testid="period-dialog">
          <DialogHeader><DialogTitle className="font-heading">Período de vacaciones</DialogTitle>
            <DialogDescription>Define cuándo inicia el período de <strong>{periodTarget?.name}</strong> y cuántos días se le renuevan cada año. Al cumplirse un año, el sistema <strong>suma automáticamente</strong> los días nuevos (se acumulan con los que no usó).</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-widest">Inicio del período</Label>
              <Input type="date" className="rounded-sm" value={periodForm.period_start} onChange={(e) => setPeriodForm({ ...periodForm, period_start: e.target.value })} data-testid="period-start-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-widest">Días por año (renovación)</Label>
              <Input type="number" min="0" step="0.5" className="rounded-sm" value={periodForm.annual_days} onChange={(e) => setPeriodForm({ ...periodForm, annual_days: e.target.value })} data-testid="period-days-input" />
            </div>
            {periodForm.period_start && <p className="text-[11px] text-muted-foreground">Próxima renovación aprox.: <span className="font-mono">{(() => { try { const d = new Date(periodForm.period_start); d.setFullYear(d.getFullYear() + 1); return d.toISOString().slice(0, 10); } catch { return "—"; } })()}</span></p>}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setPeriodTarget(null)}>Cancelar</Button>
            <Button className="rounded-sm font-bold" onClick={doPeriod} disabled={!periodForm.period_start || periodForm.annual_days === ""} data-testid="save-period-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!historyTarget} onOpenChange={(v) => !v && setHistoryTarget(null)}>
        <DialogContent className="rounded-sm max-w-md max-h-[85vh] overflow-y-auto" data-testid="history-dialog">
          <DialogHeader><DialogTitle className="font-heading">Historial de ajustes</DialogTitle>
            <DialogDescription>{historyTarget?.name}</DialogDescription></DialogHeader>
          <div className="space-y-2">
            {(historyTarget?.adjustments || []).length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">Sin ajustes.</p>}
            {(historyTarget?.adjustments || []).slice().reverse().map((a) => (
              <div key={a.id} className="flex items-start justify-between gap-3 border border-border rounded-sm p-2.5" data-testid={`adj-item-${a.id}`}>
                <div className="flex-1">
                  <p className={`font-heading font-black ${a.delta > 0 ? "text-primary" : "text-destructive"}`}>{a.delta > 0 ? "+" : ""}{a.delta} día(s)</p>
                  {a.reason && <p className="text-xs text-foreground">{a.reason}</p>}
                  <p className="text-[11px] text-muted-foreground">{a.by} · {String(a.date).slice(0, 10)}</p>
                </div>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => delAdjust(historyTarget.user_id, a.id)} data-testid={`del-adj-${a.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
          <DialogFooter><Button variant="outline" className="rounded-sm" onClick={() => setHistoryTarget(null)}>Cerrar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
